import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import type { IssueViewRelayFragment } from '@atlassian/jira-issue-fetch-services-common';
import { useFieldsValues, useIssueFieldConfig } from '@atlassian/jira-issue-field-base';
import type {
	LayoutContainerTabItem,
	LayoutContainerTemplateItem,
} from '@atlassian/jira-issue-layout-common-constants';
import { getLayoutFieldTypes } from '@atlassian/jira-platform-field-config';
import { fireOperationalAnalytics, MountEvent } from '@atlassian/jira-product-analytics-bridge';
import { StaticTabs } from './static-tabs';
import { StaticTabsSpotlight } from './static-tabs-spotlight';
import { TabsSpotlightWrapper } from './styled';
import { isTabFieldVisibleUtil, extractTabArray } from './utils';

const TemplatesTabViewOnboardingSpotlightTarget = 'TemplatesTabViewOnboardingSpotlightTarget';

type Props = {
	// TODO Decomp BENTO-12514 - add useFragment to this component and replace this prop with more specific fragment key
	issueViewRelayFragment?: IssueViewRelayFragment | null;
	items: LayoutContainerTabItem[];
	area: string;
};

export const TabView = ({ items, area, issueViewRelayFragment }: Props) => {
	const issueKey = useIssueKey();

	const [{ value: issueFieldsConfig }] = useIssueFieldConfig(issueKey);
	const [issueFieldsValues] = useFieldsValues(issueKey);
	const layoutFieldTypes = getLayoutFieldTypes();

	const isTabFieldVisible = useCallback(
		(item: LayoutContainerTemplateItem) =>
			isTabFieldVisibleUtil({ item, issueFieldsConfig, issueFieldsValues, layoutFieldTypes }),
		[issueFieldsConfig, issueFieldsValues, layoutFieldTypes],
	);

	const tabArray = extractTabArray({
		items,
		area,
		issueViewRelayFragment,
		isTabFieldVisible,
	});

	// This is an edge case and should never happen.
	// However in some situations it's possible that tabArray is empty due to async nature of data we're receiving here.
	// Normally, we'd ignore this cause eventually we get all needed data and tabArray gets the right shape,
	// but in this case we wrap tabs in React.memo to prevent unwanted re-renders. So once it was rendered with 0 tabs, it'll never re-render when the tabs are there.
	if (tabArray.length === 0) {
		return null;
	}

	// If there is just one Tab, we exit early,
	// and display the content normally.
	if (tabArray.length === 1) {
		const oneFoundTab = tabArray[0];
		return oneFoundTab && oneFoundTab.content;
	}

	return (
		<TabsWrapper>
			<TabsSpotlightWrapper>
				<SpotlightTarget name={TemplatesTabViewOnboardingSpotlightTarget}>
					<StaticTabsSpotlight tabsContent={tabArray} />
				</SpotlightTarget>
			</TabsSpotlightWrapper>
			<EngagementSpotlight engagementId={TemplatesTabViewOnboardingSpotlightTarget} />
			<MountEvent
				onMount={(analyticsEvent: UIAnalyticsEvent) =>
					fireOperationalAnalytics(analyticsEvent, 'TemplatesTabViewOnboarding mounted')
				}
			/>
			<StaticTabs tabsContent={tabArray} />
		</TabsWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabsWrapper = styled.div({
	position: 'relative',
	width: 'calc(100% + 16px)',
	// Workaround for wrapping excessive tabs until AtlasKit support it natively
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"div[role='tablist']": {
		flexWrap: 'wrap',
	},
});
